export const ThemeStorageKey = 'ui-theme';
export enum Theme {
  Light = 'light',
  Dark = 'dark',
}
export type HorizonTheme = {
  themeName: Theme;
  onChange: (themeName: Theme) => void;

  // Brand accent (now holds the platform sky-blue; token name is legacy)
  teal: string;
  tealActive: string;
  tealBackgroundColor: string;
  tealLightOpacity: string;
  // Text/icon color that sits on top of a solid `teal` action fill (e.g. primary
  // buttons). Navy in the brand reskin (legible on Sky Blue); white in legacy.
  solidActionText: string;
  // Action-colored text on light/neutral surfaces (links, active tab labels). A
  // deeper, AA-legible brand blue — distinct from the bright `teal` used for
  // solid fills and indicators.
  actionText: string;
  actionTextHover: string;

  // Count badge (SKYSPECS turquoise) — record-count indicators next to tabs
  countBadgeBackground: string;
  countBadgeText: string;

  // Background and border colors
  activeMenuItemBackgroundColor: string;
  avatarColor: string;
  borderColor: string;
  borderAccentColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  primaryButtonTextColor: string;
  carouselBackgroundColor: string;
  contrastColor: string;
  checkboxBackgroundColor: string;
  defaultStatusColor: string;
  disabledBackground: string;
  donutChartGray: string;
  gray: string;
  inputBackgroundColor: string;
  primaryBackgroundColor: string;
  secondaryBackgroundColor: string;
  selectedListItemBackground: string;
  tableAccentColor: string;
  tableBannerBackgroundColor: string;
  tableHeaderButtonColor: string;
  tertiaryBackgroundColor: string;
  alternatingTableRowColor: string;
  tooltipBackground: string;
  tooltipTextColor: string;

  // Sidebar — the navy rail (shared Horizon platform look)
  sidebarBg: string;
  sidebarBorder: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActiveBg: string;

  // Text colors
  descriptionColor: string;
  disabledTextColor: string;
  headerColor: string;
  labelColor: string;
  noneTextColor: string;
  secondaryTextColor: string;
  textColor: string;

  // Shadow colors
  darkShadow: string;
  lightShadow: string;
  overlay: string;
  noDamageOverlay: string;

  // Damage severity colors
  severityHighColor: string;
  severityMaxColor: string;

  // Measurement tool colors
  darkRed: string;
  darkTeal: string;

  // 🌈
  red: string;
  backgroundRed: string;
  lightRed: string;
  orange: string;
  yellow: string;
  green: string;
  darkGreen: string;
  purple: string;
  lightBlue: string;

  // Map UI colors
  mapControlsBackground: string;
  mapControlsBackgroundDark: string;
  mapControlsTextColor: string;
  mapControlsBorderColor: string;
  mapControlsButtonBackground: string;
  mapControlsIconColor: string;
  mapControlsIconActiveColor: string;
  mapControlsHoverRing: string;
  mapControlsShadowStrong: string;
  mapControlsShadowMedium: string;
  mapControlsShadowLight: string;
  mapPicturePinStroke: string;
  mapThumbnailShadow: string;
  mapPicturePinShadow: string;
  mapLassoActiveBackground: string;
  mapSplitResizerLine: string;
  mapSplitResizerLineHover: string;
  mapOutlineSelected: string;
  mapOutlineUnreviewed: string;
  mapOutlineHover: string;
  mapOutlineLasso: string;
  mapPictureTypeTagRgbText: string;
  mapPictureTypeTagRgbBackground: string;
  mapPictureTypeTagRgbBorder: string;
  mapPictureTypeTagThermalText: string;
  mapPictureTypeTagThermalBackground: string;
  mapPictureTypeTagThermalBorder: string;

  // Plotro colors
  plot: {
    axisColor: string;
    tickColor: string;
    bladeShapeStroke: string;
    bladeShapeFill: string;
    contourStroke: string;
    defaultPointColor: string;
    pointColors: string[];
    opacity: { less: number; default: number; more: number };
    slicedViewFill: string;
    slicedViewLegend: string;
  };
  // Temperature Measurement tool colors
  measurementTextBorder: string;
  measurementTextBoxShadow: string;
  measurementTextColor: string;
  measurementReferenceRectColor: string;
  measurementMainRectColor: string;
  measurementPreviewRectColor: string;
  measurementFocusFillColor: string;
  opSeverity: {
    bloodRed: string;
    crimson: string;
    redAnchor: string;
    darkBurntOrange: string;
    deepOrange: string;
    pureOrange: string;
  };
};

/**
 * Legacy SkySpecs teal. No longer the antd primary (Horizon now uses the
 * platform sky-blue below); kept as the canonical teal value for reference.
 * In general, all colors should be accessed through the theme as described in
 * `./THEME.md`.
 */
export const SKYSPECS_TEAL = '#24a4ab';

/**
 * Horizon platform structural palette — shared with the horizon-grid app so the
 * two SkySpecs front-ends read as one platform. Navy is the structural brand
 * color and teal is the count-badge/secondary accent. The action color now
 * comes from the SkySpecs Brand Guidelines V3 palette below.
 */
export const HZ_NAVY = '#061F32';
export const HZ_TEAL = '#34A4AB';

/**
 * SkySpecs Brand Guidelines V3 palette. Sky Blue is the primary brand color,
 * but it's bright — white text on it fails WCAG AA — so it's used as an accent
 * (tints, borders, badges with dark text) rather than as the action fill on
 * light surfaces. Current Blue is the AA-safe action color on white; on the
 * dark (navy) theme Sky Blue becomes the action color since it clears AA there.
 */
export const BRAND_SKY_BLUE = '#30AEE4';
export const BRAND_NAVY = HZ_NAVY; // '#061F32'
export const BRAND_SPROUT = '#4BDDB0';
export const BRAND_CURRENT_BLUE = '#09637E';
// Darker Sky Blue for hover/press on solid Sky Blue surfaces (both themes).
export const BRAND_SKY_BLUE_HOVER = '#1c96c9';
export const BRAND_GOLD = '#FBBA00';
export const BRAND_LIGHT_GREY = '#F6F7FE';
export const BRAND_MAGENTA = '#CA2C92';
export const BRAND_RED = '#E21D00';
export const BRAND_LIME = '#9CCB3B';
// Deepened Lime for legible semantic success — green icons, solid status tags
// (white text), and the "good" end of severity ramps. The bright BRAND_LIME
// stays for chart fills and reads well on the dark theme's navy surfaces.
export const BRAND_GREEN = '#5f8f14';
export const BRAND_GREEN_DARK = '#4a7010';
export const BRAND_GREY_LIGHT = '#CDCAD2';
export const BRAND_GREY_MID = '#6A7489';

/** Energy Gradient (Sky Blue → Sprout Green → Gold) — a brand accent for graphic surfaces. */
export const BRAND_ENERGY_GRADIENT = 'linear-gradient(90deg, #30AEE4, #4BDDB0, #FBBA00)';

// Sky Blue tint helper for accent backgrounds/rings (48, 174, 228).
const skyBlueTint = (alpha: number) => `rgba(48, 174, 228, ${alpha})`;

/** Brand chart series — bright, distinguishable brand hues for data viz. */
const BRAND_CHART_COLORS = [
  BRAND_SKY_BLUE,
  BRAND_SPROUT,
  BRAND_GOLD,
  BRAND_MAGENTA,
  BRAND_LIME,
  BRAND_CURRENT_BLUE,
  BRAND_GREY_MID,
];

export const Themes: { [key in Theme]: Omit<HorizonTheme, 'onChange'> } = {
  [Theme.Light]: {
    themeName: Theme.Light,

    // Brand action color — Sky Blue leads (Brand Guidelines V3). Solid fills pair
    // with navy text (`solidActionText`) so they stay legible.
    teal: BRAND_SKY_BLUE,
    tealActive: BRAND_SKY_BLUE_HOVER,
    tealBackgroundColor: skyBlueTint(0.1),
    tealLightOpacity: skyBlueTint(0.2),
    solidActionText: BRAND_NAVY,
    actionText: BRAND_CURRENT_BLUE,
    actionTextHover: '#074d63',

    // Count badge — Sky Blue accent with navy text (high-contrast brand pop)
    countBadgeBackground: BRAND_SKY_BLUE,
    countBadgeText: BRAND_NAVY,

    // Background and border colors
    activeMenuItemBackgroundColor: skyBlueTint(0.1),
    avatarColor: BRAND_SPROUT,
    borderColor: BRAND_GREY_LIGHT,
    borderAccentColor: 'rgba(6, 31, 50, 0.1)',
    buttonBackgroundColor: '#fff',
    buttonBorderColor: '#c0d0dd',
    primaryButtonTextColor: '#ffffff',
    carouselBackgroundColor: '#ffffffd9',
    contrastColor: BRAND_NAVY,
    checkboxBackgroundColor: 'unset',
    defaultStatusColor: '#7a9bb5',
    disabledBackground: '#eef3f7',
    donutChartGray: '#e6e6e6',
    gray: '#d4dfe8',
    inputBackgroundColor: '#fff',
    primaryBackgroundColor: BRAND_LIGHT_GREY,
    secondaryBackgroundColor: '#fff',
    selectedListItemBackground: skyBlueTint(0.1),
    tableAccentColor: '#e6f5f6',
    tableBannerBackgroundColor: 'lightyellow',
    tableHeaderButtonColor: '#7a9bb5',
    tertiaryBackgroundColor: '#fafafa',
    alternatingTableRowColor: '#f8fafc',
    tooltipBackground: BRAND_NAVY,
    tooltipTextColor: 'rgba(255, 255, 255, 0.9)',

    // Sidebar — solid navy rail (energy accent strip applied in SideNav.style)
    sidebarBg: BRAND_NAVY,
    sidebarBorder: '#0c2d46',
    sidebarText: '#c0d0dd',
    sidebarTextMuted: '#7a9bb5',
    sidebarActiveBg: skyBlueTint(0.18),

    // Text colors — Horizon navy scale
    descriptionColor: BRAND_GREY_MID,
    disabledTextColor: '#b3c2cf',
    headerColor: BRAND_NAVY,
    labelColor: BRAND_GREY_MID,
    noneTextColor: '#7a9bb5',
    secondaryTextColor: BRAND_GREY_MID,
    textColor: BRAND_NAVY,

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.1)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(0,0,0,0.9)',
    noDamageOverlay: 'rgba(0,0,0,0.7)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // Measurement tool colors
    darkRed: 'rgba(198, 21, 21, 1)',
    darkTeal: 'rgba(2, 137, 139, 0.945)',

    // 🌈 — status hues aligned to the SkySpecs brand palette
    red: BRAND_RED,
    backgroundRed: '#fef2f2',
    lightRed: 'rgba(226, 29, 0, 0.6)',
    orange: '#ea580c',
    yellow: BRAND_GOLD,
    green: BRAND_GREEN,
    darkGreen: BRAND_GREEN_DARK,
    purple: BRAND_MAGENTA,
    lightBlue: '#9bcce3',

    // Map UI colors
    mapControlsBackground: 'rgba(0, 0, 0, 0.35)',
    mapControlsBackgroundDark: 'rgba(0, 0, 0, 0.8)',
    mapControlsTextColor: 'rgba(255, 255, 255, 0.9)',
    mapControlsBorderColor: 'rgba(255, 255, 255, 0.35)',
    mapControlsButtonBackground: 'rgba(0, 0, 0, 0.9)',
    mapControlsIconColor: '#ffffff',
    mapControlsIconActiveColor: '#000000',
    mapControlsHoverRing: skyBlueTint(0.25),
    mapControlsShadowStrong: 'rgba(0, 0, 0, 0.45)',
    mapControlsShadowMedium: 'rgba(0, 0, 0, 0.5)',
    mapControlsShadowLight: 'rgba(0, 0, 0, 0.3)',
    mapPicturePinStroke: '#000000',
    mapThumbnailShadow: 'rgba(0, 0, 0, 0.25)',
    mapPicturePinShadow: 'rgba(0, 0, 0, 0.35)',
    mapLassoActiveBackground: 'rgba(255, 212, 0, 0.2)',
    mapSplitResizerLine: 'rgba(255, 255, 255, 0.15)',
    mapSplitResizerLineHover: 'rgba(255, 255, 255, 0.35)',
    mapOutlineSelected: '#1E5AA8',
    mapOutlineUnreviewed: '#CFCFCF',
    mapOutlineHover: '#FFD400',
    mapOutlineLasso: '#FFD400',
    mapPictureTypeTagRgbText: '#0b3b73',
    mapPictureTypeTagRgbBackground: '#d6ebff',
    mapPictureTypeTagRgbBorder: '#7cb8ff',
    mapPictureTypeTagThermalText: '#6b1b04',
    mapPictureTypeTagThermalBackground: '#ffe1d6',
    mapPictureTypeTagThermalBorder: '#ff9e78',

    // Plotro colors
    plot: {
      axisColor: '#0000008c',
      tickColor: '#0000001a',
      bladeShapeFill: '#f7f7f7',
      bladeShapeStroke: '#0000001f',
      contourStroke: '#0000000d',
      defaultPointColor: '#8c8c8c',
      pointColors: BRAND_CHART_COLORS,
      opacity: { less: 0.2, default: 0.6, more: 0.8 },
      slicedViewFill: '#CFE2F3',
      slicedViewLegend: '#E0C2AA',
    },
    // Temperature Measurement tool colors
    measurementTextBorder: 'rgba(255, 255, 255, 0.4)',
    measurementTextBoxShadow: 'rgba(0, 0, 0, 0.35)',
    measurementTextColor: '#ffffff',
    measurementReferenceRectColor: '#39FF14',
    measurementPreviewRectColor: '#3B82F6',
    measurementMainRectColor: '#EF4444',
    measurementFocusFillColor: '#F5E0C5',
    opSeverity: {
      bloodRed: '#8B0000',
      crimson: '#C50000',
      redAnchor: '#FF0000',
      darkBurntOrange: '#E55000',
      deepOrange: '#F27B00',
      pureOrange: '#FFA500',
    },
  },
  [Theme.Dark]: {
    themeName: Theme.Dark,

    // Brand action color — Sky Blue leads on the dark navy surfaces too. Solid
    // fills pair with navy text (`solidActionText`) for legibility.
    teal: BRAND_SKY_BLUE,
    tealActive: BRAND_SKY_BLUE_HOVER,
    tealBackgroundColor: skyBlueTint(0.15),
    tealLightOpacity: skyBlueTint(0.25),
    solidActionText: BRAND_NAVY,
    actionText: BRAND_SKY_BLUE,
    actionTextHover: '#5cc0ea',

    // Count badge — Sky Blue accent with navy text (high-contrast brand pop)
    countBadgeBackground: BRAND_SKY_BLUE,
    countBadgeText: BRAND_NAVY,

    // Background and border colors — Horizon navy surfaces
    activeMenuItemBackgroundColor: skyBlueTint(0.18),
    avatarColor: BRAND_SPROUT,
    borderColor: '#2a5578',
    borderAccentColor: 'rgba(152, 181, 202, 0.15)',
    buttonBackgroundColor: '#103750',
    buttonBorderColor: '#1e4a6b',
    primaryButtonTextColor: '#ffffff',
    carouselBackgroundColor: 'rgba(0, 0, 0, 0.6)',
    contrastColor: '#fff',
    checkboxBackgroundColor: '#0c2d46',
    defaultStatusColor: '#5e8ba5',
    disabledBackground: '#0c2d46',
    donutChartGray: '#e6e6e6',
    gray: '#5e8ba5',
    inputBackgroundColor: '#0c2d46',
    primaryBackgroundColor: BRAND_NAVY,
    secondaryBackgroundColor: '#0c2d46',
    selectedListItemBackground: skyBlueTint(0.15),
    tableAccentColor: '#0c3a4a',
    tableBannerBackgroundColor: '#aa8c2b',
    tableHeaderButtonColor: '#5e8ba5',
    tertiaryBackgroundColor: '#123a55',
    alternatingTableRowColor: '#0a2740',
    tooltipBackground: '#103750',
    tooltipTextColor: 'rgba(255, 255, 255, 0.9)',

    // Sidebar — solid navy rail (energy accent strip applied in SideNav.style)
    sidebarBg: '#041522',
    sidebarBorder: '#0c2d46',
    sidebarText: '#c0d0dd',
    sidebarTextMuted: '#7a9bb5',
    sidebarActiveBg: skyBlueTint(0.18),

    // Text colors — Horizon navy scale (dark)
    descriptionColor: '#98b5ca',
    disabledTextColor: '#5e8ba5',
    headerColor: '#f0f5f9',
    labelColor: '#98b5ca',
    noneTextColor: '#5e8ba5',
    secondaryTextColor: '#98b5ca',
    textColor: '#f0f5f9',

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.6)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(66, 66, 68, .75)',
    noDamageOverlay: 'rgba(0, 0, 0, 0.7)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // Measurement tool colors
    darkRed: 'rgba(198, 21, 21, 1)',
    darkTeal: 'rgba(2, 137, 139, 0.945)',

    // 🌈 — status hues aligned to the SkySpecs brand palette (dark)
    red: '#f87171',
    backgroundRed: '#3a1a1a',
    lightRed: 'rgba(248, 113, 113, 0.6)',
    orange: '#fb923c',
    yellow: BRAND_GOLD,
    green: BRAND_LIME,
    darkGreen: '#7cae2c',
    purple: '#d95bb5',
    lightBlue: '#2e7ea3',

    // Map UI colors
    mapControlsBackground: 'rgba(0, 0, 0, 0.35)',
    mapControlsBackgroundDark: 'rgba(0, 0, 0, 0.8)',
    mapControlsTextColor: 'rgba(255, 255, 255, 0.9)',
    mapControlsBorderColor: 'rgba(255, 255, 255, 0.35)',
    mapControlsButtonBackground: 'rgba(0, 0, 0, 0.9)',
    mapControlsIconColor: '#ffffff',
    mapControlsIconActiveColor: '#000000',
    mapControlsHoverRing: skyBlueTint(0.3),
    mapControlsShadowStrong: 'rgba(0, 0, 0, 0.45)',
    mapControlsShadowMedium: 'rgba(0, 0, 0, 0.5)',
    mapControlsShadowLight: 'rgba(0, 0, 0, 0.3)',
    mapPicturePinStroke: '#000000',
    mapThumbnailShadow: 'rgba(0, 0, 0, 0.25)',
    mapPicturePinShadow: 'rgba(0, 0, 0, 0.35)',
    mapLassoActiveBackground: 'rgba(255, 212, 0, 0.2)',
    mapSplitResizerLine: 'rgba(255, 255, 255, 0.15)',
    mapSplitResizerLineHover: 'rgba(255, 255, 255, 0.35)',
    mapOutlineSelected: '#1E5AA8',
    mapOutlineUnreviewed: '#CFCFCF',
    mapOutlineHover: '#FFD400',
    mapOutlineLasso: '#FFD400',
    mapPictureTypeTagRgbText: '#0b3b73',
    mapPictureTypeTagRgbBackground: '#d6ebff',
    mapPictureTypeTagRgbBorder: '#7cb8ff',
    mapPictureTypeTagThermalText: '#6b1b04',
    mapPictureTypeTagThermalBackground: '#ffe1d6',
    mapPictureTypeTagThermalBorder: '#ff9e78',

    // Plotro colors
    plot: {
      axisColor: '#b2b0b2ff',
      tickColor: '#b2b0b26a',
      bladeShapeFill: '#66666B80',
      bladeShapeStroke: '#a2a0a21f',
      contourStroke: '#a2a0a22d',
      defaultPointColor: '#f5f4f3',
      pointColors: BRAND_CHART_COLORS,
      opacity: { less: 0.4, default: 0.8, more: 0.99 },
      slicedViewFill: '#CFE2F3',
      slicedViewLegend: '#E0C2AA',
    },
    // Temperature Measurement tool colors
    measurementTextBorder: 'rgba(255, 255, 255, 0.4)',
    measurementTextBoxShadow: 'rgba(0, 0, 0, 0.35)',
    measurementTextColor: '#ffffff',
    measurementReferenceRectColor: '#39FF14',
    measurementPreviewRectColor: '#3B82F6',
    measurementMainRectColor: '#EF4444',
    measurementFocusFillColor: '#F5E0C5',
    opSeverity: {
      bloodRed: '#8B0000',
      crimson: '#C50000',
      redAnchor: '#FF0000',
      darkBurntOrange: '#E55000',
      deepOrange: '#F27B00',
      pureOrange: '#FFA500',
    },
  },
};
